import clsx from 'clsx';
import { FC, ReactNode } from 'react';
import { Helmet } from 'react-helmet';
import { Container, Section } from '../components/Base';
import { getTitle } from '../utils/document';
import { FORMATTING_TOKENS_ROWS } from './FormattingTokensData';

const FormattingTokens: React.FC = () => {
  const cellClassName =
    'border border-solid border-gray-300 py-1 px-px sm:px-2';

  return (
    <div>
      <Helmet>
        <title>{getTitle('File date formatting tokens')}</title>
      </Helmet>
      <Section>
        <Container>
          <div className="gfp-row">
            <div className="col-8 offset-2">
              <h1>Filename date formatting</h1>
            </div>
          </div>
        </Container>
        <Container className="pb-10 text-left">
          <div className="col-10 offset-1 mb-6 text-sm">
            <p>
              Premium users can custom format their filenames in the extension.
              Manage this by right-clicking on the extension and selecting
              “Options” or by clicking the gear icon inside the extension. You
              can use the following tokens with{' '}
              <Code className="nowrap">{'{date:<tokens>}'}</Code> format text,
              such as{' '}
              <span className="nowrap">
                <Code>{'{date:YYYY-MM-dd}'}</Code>.
              </span>
            </p>
            <p>
              This functionality and the info below comes from the open-source{' '}
              <a
                className="dim"
                href="https://moment.github.io/luxon/#/formatting?id=table-of-tokens"
              >
                luxon project
              </a>
              :
            </p>
            <p className="italic">
              Some tokens have a “standalone” and “format” version. Some
              languages require different forms of a word based on whether it is
              part of a longer phrase or just by itself (e.g. “Monday the 22nd”
              vs “Monday”). Use them accordingly.
            </p>
            <p className="italic">
              Some of the formats are “macros”, meaning they correspond to
              multiple components. These use the native Intl API and will order
              their constituent parts in a locale-friendly way (e.g., “f”).
            </p>
          </div>
          <div className="col-10 offset-1">
            <table className="border-collapse text-[10px] sm:text-sm">
              <thead>
                <tr>
                  <th className={cellClassName}>Standalone token</th>
                  <th className={cellClassName}>Format token</th>
                  <th className={cellClassName}>Description</th>
                  <th className={cellClassName}>Example</th>
                </tr>
              </thead>
              <tbody>
                {FORMATTING_TOKENS_ROWS.map((row) => (
                  <tr key={row.tok} className="even:bg-gray-100">
                    <td className={cellClassName}>{row.tok}</td>
                    <td className={cellClassName}>{row.ftok}</td>
                    <td className={cellClassName}>{row.desc}</td>
                    <td className={cellClassName}>
                      <Code className="break-all text-xs">{row.ex}</Code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Container>
      </Section>
    </div>
  );
};

export default FormattingTokens;

// ## Helpers

const Code: FC<{ className?: string; children: ReactNode }> = ({
  className,
  children,
}) => {
  return (
    <code
      className={clsx(
        'inline-block rounded-sm bg-gray-200 px-1 py-0.5 font-mono text-orange-600',
        className,
      )}
    >
      {children}
    </code>
  );
};
